import { useCallback, useEffect, useMemo, useState } from "react";

const TONES = ["Professional", "Casual", "Persuasive", "SEO Optimized"];

function IconSparkles({ className = "" }) {
  return (
    <svg className={className} width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l1.2 3.6L17 7l-3.8 1.4L12 12l-1.2-3.6L7 7l3.8-1.4L12 2zM19 15l.7 2.1L22 18l-2.3.9L19 21l-.7-2.1L16 18l2.3-.9L19 15zM5 14l.8 2.4L8 17l-2.2.8L5 20l-.8-2.4L2 17l2.2-.8L5 14z"
        fill="currentColor"
        opacity="0.92"
      />
    </svg>
  );
}

function IconWand({ className = "" }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 4l5 5M12.5 6.5l3 3M4 20l7.5-7.5M9.5 12.5l3 3" strokeLinecap="round" />
      <path d="M14 4l2 2" strokeLinecap="round" />
    </svg>
  );
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function buildVariation({
  name,
  category,
  features,
  tone,
  audience,
  keywords,
  shortDescription,
  bulletPoints,
  variationIndex
}) {
  const kw = keywords
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const kwLine = kw.length ? kw.join(", ") : "quality, value, trusted store";
  const feats = features.length ? features : ["Premium build", "Thoughtful design", "Reliable everyday use"];
  const aud = audience.trim() || "modern shoppers";

  const intros = {
    Professional: `## ${name}\n\n**${name}** is a refined **${category || "catalog"}** offering engineered for consistency and long-term satisfaction.`,
    Casual: `## ${name}\n\nSay hello to **${name}** — the **${category || "shop"}** pick that feels effortless to use and easy to recommend.`,
    Persuasive: `## ${name}\n\n**${name}** helps **${aud}** get more from every purchase — a standout **${category || "selection"}** with real-world benefits you can feel.`,
    "SEO Optimized": `## ${name} — ${category || "Online"} | ${kwLine}\n\nShop **${name}** online. **${category || "Quality goods"}** with secure checkout and clear product details. Keywords: *${kwLine}*.`
  };

  const toneKey = TONES.includes(tone) ? tone : "Professional";
  let intro = intros[toneKey];
  if (variationIndex === 1) intro = intro.replace("## ", "## ").replace(/\*\*(.+?)\*\*/g, "**$1**") + `\n\n*Crafted detail and dependable performance — variation B.*`;
  if (variationIndex === 2) intro += `\n\n*A fresh angle: emphasize convenience, care, and confidence at checkout — variation C.*`;

  if (shortDescription) {
    const short = `**${name}** · ${category || "Premium"} · ${feats.slice(0, 2).join(" · ")}`;
    return `${short}\n\n${feats.map((f) => `- ${f}`).join("\n")}`;
  }

  if (bulletPoints) {
    return `${intro}\n\n### Key points\n\n${feats.map((f) => `- **${f}** — ideal for ${aud}.`).join("\n")}\n\n### Spec snapshot\n\n| Fit | ${aud} |\n| --- | --- |\n| Category | ${category || "—"} |`;
  }

  return `${intro}\n\n### Why it works\n\n${feats
    .map((f) => `### ${f}\n\nDesigned with **${aud}** in mind — balanced materials, clear benefits, and a polished experience.\n`)
    .join("\n")}\n### Care & trust\n\n- Quality-checked before dispatch\n- Clear descriptions and fair policies\n- Keywords: *${kwLine}*`;
}

function buildMeta(name, category, keywords, tone) {
  const kw = keywords
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 4);
  const title = `${name}${category ? ` | ${category}` : ""}${kw.length ? ` — ${kw[0]}` : ""}`.slice(0, 58);
  const desc = `${tone} tone: Discover ${name}. ${category ? `${category}. ` : ""}${kw.join(", ") || "Shop with confidence."}`.slice(0, 155);
  return { title, description: desc };
}

/**
 * @param {object} props
 * @param {string[]} props.categories
 * @param {boolean} [props.compact]
 * @param {string} [props.prefillName]
 * @param {string} [props.prefillCategory]
 * @param {(text: string) => void} props.onApplyDescription
 */
export default function AIProductDescriptionGenerator({
  categories = [],
  compact = false,
  prefillName = "",
  prefillCategory = "",
  onApplyDescription
}) {
  const [productName, setProductName] = useState(prefillName || "");
  const [category, setCategory] = useState(prefillCategory || "");
  const [tagInput, setTagInput] = useState("");
  const [features, setFeatures] = useState([]);
  const [tone, setTone] = useState("Professional");
  const [audience, setAudience] = useState("");
  const [keywords, setKeywords] = useState("");
  const [shortDescription, setShortDescription] = useState(false);
  const [bulletPoints, setBulletPoints] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [typingLine, setTypingLine] = useState("");
  const [variations, setVariations] = useState(["", "", ""]);
  const [activeVariation, setActiveVariation] = useState(0);
  const [resultVisible, setResultVisible] = useState(false);
  const [meta, setMeta] = useState({ title: "", description: "" });
  const [rewriteSource, setRewriteSource] = useState("");

  useEffect(() => {
    if (prefillName) setProductName(prefillName);
  }, [prefillName]);

  useEffect(() => {
    if (prefillCategory) setCategory(prefillCategory);
  }, [prefillCategory]);

  const addTag = useCallback(() => {
    const t = tagInput.trim();
    if (!t || features.includes(t)) return;
    setFeatures((prev) => [...prev, t]);
    setTagInput("");
  }, [tagInput, features]);

  const removeTag = (t) => setFeatures((prev) => prev.filter((x) => x !== t));

  const runGenerate = useCallback(
    async (isRegenerate) => {
      const name = productName.trim() || "Your product";
      const cat = category || categories[0] || "General";
      setGenerating(true);
      setResultVisible(false);
      setTypingLine("");
      const steps = ["Analyzing inputs…", "Shaping tone & structure…", "Polishing for conversions…"];
      for (let i = 0; i < steps.length; i++) {
        setTypingLine(steps[i]);
        await delay(480);
      }
      const next = [0, 1, 2].map((i) =>
        buildVariation({
          name,
          category: cat,
          features,
          tone,
          audience,
          keywords,
          shortDescription,
          bulletPoints,
          variationIndex: i
        })
      );
      setVariations(next);
      setActiveVariation(0);
      setMeta(buildMeta(name, cat, keywords, tone));
      setGenerating(false);
      setTypingLine("");
      setResultVisible(true);
      if (isRegenerate) {
        /* keep selection */
      }
    },
    [productName, category, categories, features, tone, audience, keywords, shortDescription, bulletPoints]
  );

  const currentText = variations[activeVariation] || "";

  const copyCurrent = () => {
    if (!currentText) return;
    navigator.clipboard?.writeText(currentText).catch(() => {});
  };

  const applyToForm = () => {
    if (currentText) onApplyDescription(currentText);
  };

  const runRewrite = async () => {
    const src = rewriteSource.trim() || currentText;
    if (!src) return;
    setGenerating(true);
    setTypingLine("Rewriting for clarity and punch…");
    await delay(900);
    const improved = `${src.trim()}\n\n---\n✨ **Refined:** Tighter sentences, stronger verbs, and clearer benefits — tuned for ${tone.toLowerCase()} readability while keeping your facts intact.`;
    setVariations([improved, `${improved}\n\n*Alternate hook:* Lead with outcomes, then proof points.`, `${improved}\n\n*Alternate hook:* Lead with trust signals, then specs.`]);
    setActiveVariation(0);
    setMeta(buildMeta(productName || "Product", category, keywords, tone));
    setGenerating(false);
    setTypingLine("");
    setResultVisible(true);
  };

  const catOptions = useMemo(() => categories.filter(Boolean), [categories]);

  return (
    <div className={`ai-gen-card glass-panel ${compact ? "ai-gen-card--compact" : ""}`}>
      <div className="ai-gen-header">
        <div className="ai-gen-title-row">
          <span className="ai-gen-icon-wrap" aria-hidden>
            <IconSparkles className="ai-gen-sparkle" />
            <IconWand className="ai-gen-wand" />
          </span>
          <div>
            <h3 className="ai-gen-title">AI Product Description Generator</h3>
            <p className="ai-gen-sub">Draft premium copy in seconds — tuned to tone, audience, and SEO.</p>
          </div>
        </div>
      </div>

      <div className="ai-gen-fields">
        <label className="ai-gen-label">
          Product name
          <input
            className="ai-gen-input"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="e.g. Matte black insulated bottle"
          />
        </label>
        <label className="ai-gen-label">
          Category
          <select className="ai-gen-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Select category</option>
            {catOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="ai-gen-label ai-gen-label--full">
          Key features
          <div className="ai-tag-row">
            <input
              className="ai-gen-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              placeholder="Type a feature, press Enter"
            />
            <button type="button" className="ai-gen-btn-secondary" onClick={addTag}>
              Add
            </button>
          </div>
          <div className="ai-tag-list">
            {features.map((t) => (
              <button key={t} type="button" className="ai-tag" onClick={() => removeTag(t)}>
                {t} ×
              </button>
            ))}
          </div>
        </label>
        <label className="ai-gen-label">
          Tone
          <select className="ai-gen-input" value={tone} onChange={(e) => setTone(e.target.value)}>
            {TONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="ai-gen-label">
          Target audience <span className="ai-optional">(optional)</span>
          <input
            className="ai-gen-input"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="e.g. small business owners"
          />
        </label>
        <label className="ai-gen-label ai-gen-label--full">
          Keywords <span className="ai-optional">(SEO)</span>
          <input
            className="ai-gen-input"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="comma separated: insulated, BPA free, 750ml"
          />
        </label>
        <div className="ai-gen-toggles ai-gen-label--full">
          <label className="ai-toggle">
            <input type="checkbox" checked={shortDescription} onChange={(e) => setShortDescription(e.target.checked)} />
            Short description
          </label>
          <label className="ai-toggle">
            <input type="checkbox" checked={bulletPoints} onChange={(e) => setBulletPoints(e.target.checked)} />
            Bullet points
          </label>
        </div>
      </div>

      <button
        type="button"
        className="ai-gen-submit"
        disabled={generating}
        onClick={() => runGenerate(false)}
      >
        {generating ? (
          <span className="ai-gen-loading">
            <span className="ai-dot" />
            <span className="ai-dot" />
            <span className="ai-dot" />
          </span>
        ) : null}
        <span className={generating ? "ai-gen-submit-label ai-shimmer" : "ai-gen-submit-label"}>Generate with AI ✨</span>
      </button>

      {typingLine ? <p className="ai-typing">{typingLine}</p> : null}

      <div className={`ai-gen-output ${resultVisible ? "ai-gen-output--visible" : ""}`}>
        <div className="ai-variation-tabs">
          {[0, 1, 2].map((i) => (
            <button
              key={i}
              type="button"
              className={`ai-var-tab ${activeVariation === i ? "active" : ""}`}
              onClick={() => setActiveVariation(i)}
            >
              Option {i + 1}
            </button>
          ))}
        </div>
        <label className="ai-gen-label ai-gen-label--full">
          Generated description
          <textarea
            className="ai-gen-textarea"
            rows={compact ? 10 : 14}
            value={currentText}
            onChange={(e) => {
              const v = e.target.value;
              setVariations((prev) => prev.map((p, i) => (i === activeVariation ? v : p)));
            }}
            placeholder="Generated Markdown will appear here…"
          />
        </label>
        <div className="ai-gen-actions">
          <button type="button" className="ai-gen-btn-secondary" onClick={copyCurrent} disabled={!currentText}>
            Copy
          </button>
          <button type="button" className="ai-gen-btn-secondary" onClick={() => runGenerate(true)} disabled={generating}>
            Regenerate
          </button>
          <button type="button" className="ai-gen-btn-primary" onClick={applyToForm} disabled={!currentText}>
            Use in product form
          </button>
        </div>
        <div className="ai-meta-block glass-panel glass-panel--inner">
          <h4 className="ai-meta-title">SEO meta</h4>
          <p className="ai-meta-line">
            <strong>Title:</strong> {meta.title || "—"}
          </p>
          <p className="ai-meta-line">
            <strong>Description:</strong> {meta.description || "—"}
          </p>
        </div>
        <label className="ai-gen-label ai-gen-label--full">
          Rewrite / improve (paste any text)
          <textarea
            className="ai-gen-textarea ai-gen-textarea--sm"
            rows={3}
            value={rewriteSource}
            onChange={(e) => setRewriteSource(e.target.value)}
            placeholder="Optional: paste existing copy to refine"
          />
        </label>
        <button type="button" className="ai-gen-btn-secondary ai-gen-btn-full" onClick={runRewrite} disabled={generating}>
          Rewrite / Improve text
        </button>
      </div>
    </div>
  );
}
